import Loadable from 'react-loadable';

import { packagesAvailabilityResource } from 'src/components/packages/resources/packages-availability-resource';
import { packagesResource } from 'src/components/packages/resources/packages-resource';
import { LayoutLoadingPage, LoadingPage } from 'src/components/react-loadable';
import { customMergeChecksConfigRouteResource } from 'src/components/settings/merge-checks/resources';
import { loadGlobalResource } from 'src/redux/global/resources/load-global';
import { forgeProjectNavigationResource } from 'src/sections/project/forge-project-navigation-resource';
import { projectNavigationResource } from 'src/sections/project/project-navigation-resource';
import { ProjectSettingsAccessKeysLoadable } from 'src/sections/project/sections/admin/components/settings-pages/access-keys';
import { ProjectSettingsAccessTokensLoadable } from 'src/sections/project/sections/admin/components/settings-pages/access-tokens';
import { ProjectSettingsBranchRestrictionsLoadable } from 'src/sections/project/sections/admin/components/settings-pages/branch-restrictions';
import { ProjectSettingsBranchingModelLoadable } from 'src/sections/project/sections/admin/components/settings-pages/branching-model';
import { ProjectSettingsDefaultReviewersLoadable } from 'src/sections/project/sections/admin/components/settings-pages/default-reviewers';
import { ProjectSettingsDetailsLoadable } from 'src/sections/project/sections/admin/components/settings-pages/details';
import { ProjectSettingsMergeChecksLoadable } from 'src/sections/project/sections/admin/components/settings-pages/merge-checks';
import { ProjectSettingsMergeStrategiesLoadable } from 'src/sections/project/sections/admin/components/settings-pages/merge-strategies';
import { ProjectSettingsPermissionsLoadable } from 'src/sections/project/sections/admin/components/settings-pages/permissions';
import { loadProjectSettingsPermissionsResource } from 'src/sections/project/sections/admin/components/settings-pages/permissions/resources';
import { ProjectRepositoriesLoadable } from 'src/sections/project/sections/repositories';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';

import { ROUTE_NAME, Route, RouteFeatures } from './types';

const ProjectLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () => import('src/sections/project/layout'),
});

const PackagesPage = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/project/sections/packages/packages-page').then(
      m => m.ProjectPackagesPage
    ),
});

const ProjectSideNavigationLoadable = Loadable<{}, never>({
  loading: LoadingPage,
  loader: () => import('src/app/nav-system/side-nav/project-admin'),
});

const getDefaultResources = () => {
  return [
    loadGlobalResource,
    projectNavigationResource,
    forgeProjectNavigationResource,
  ];
};

const ProjectSettingsForgePage = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/forge/project-settings-menu-page/project-settings-menu-page'),
});

export const projectRoutes = (_features: RouteFeatures): Route[] => {
  const defaultResources = getDefaultResources();

  const routes: Route[] = [
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_ACCESS_KEYS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/access-keys',
      exact: true,
      // @ts-ignore
      component: ProjectSettingsAccessKeysLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_REPOSITORIES,
      path: '/:workspaceSlug/workspace/projects/:projectKey',
      exact: true,
      component: ProjectRepositoriesLoadable,
      layout: ProjectLayout,
      // workspaceNavigationResource is required for create links
      // in ./src/sections/project/sections/repositories/project-repositories-empty.tsx
      // XXX this should get switched to use the project nav resource instead
      resources: [...defaultResources, workspaceNavigationResource],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_DETAILS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/',
      exact: true,
      component: ProjectSettingsDetailsLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_DEFAULT_REVIEWERS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/pullrequests/reviewers',
      exact: false,
      component: ProjectSettingsDefaultReviewersLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_BRANCHING_MODEL,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/branching-model',
      exact: false,
      // @ts-ignore
      component: ProjectSettingsBranchingModelLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_MERGE_STRATEGIES,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/merge-strategies',
      exact: false,
      // @ts-ignore
      component: ProjectSettingsMergeStrategiesLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_ACCESS_TOKENS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/access-tokens',
      exact: true,
      component: ProjectSettingsAccessTokensLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_CUSTOM_MERGE_CHECKS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/merge-checks',
      exact: true,
      // @ts-ignore
      component: ProjectSettingsMergeChecksLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources, customMergeChecksConfigRouteResource],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_PERMISSIONS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/permissions',
      exact: true,
      component: ProjectSettingsPermissionsLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources, loadProjectSettingsPermissionsResource],
    },
    {
      name: ROUTE_NAME.PROJECT_SETTINGS_BRANCH_RESTRICTIONS,
      path: '/:workspaceSlug/workspace/projects/:projectKey/settings/branch-restrictions',
      exact: false,
      component: ProjectSettingsBranchRestrictionsLoadable,
      layout: ProjectLayout,
      sideNavigation: ProjectSideNavigationLoadable,
      resources: [...defaultResources],
    },
  ];

  routes.push({
    name: ROUTE_NAME.PROJECT_PACKAGES,
    path: '/:workspaceSlug/workspace/projects/:projectKey/packages',
    exact: true,
    component: PackagesPage,
    layout: ProjectLayout,
    resources: [
      ...defaultResources,
      workspaceNavigationResource,
      packagesResource,
      packagesAvailabilityResource,
    ],
  });

  routes.push({
    name: ROUTE_NAME.PROJECT_SETTINGS_FORGE_PAGE,
    path: '/:workspaceSlug/workspace/projects/:projectKey/settings/forge/:appId/:envKey/:moduleKey/:modulePath*',
    exact: true,
    component: ProjectSettingsForgePage,
    layout: ProjectLayout,
    sideNavigation: ProjectSideNavigationLoadable,
    resources: [...defaultResources],
  });

  return routes;
};
